import { ref } from '@vue/composition-api';
import { EventData, EventCallback, EVENT_TYPE } from '../types';
import { IOutboundMessageData } from '@onereach/ws-client';

const WORKER_BUNDLE = 'ws-events-worker.min.js';
const context = {
  isDebug: false,
  workerObjectURL: '' as any,
  worker: {} as Worker,
  connectionId: ref(''),
  /** Callbacks are NOT cleared between worker initializations! */
  callbacks: {} as Record<string, Array<EventCallback>>,
};

export const useWSEventsWorker = () => {
  const HITL_UI_SERVICE = 'hitl';

  /**
   * At least one URL should be provided.
   * Make a request to discovery service to get URL prefix.
   * Bundle name looks like ".../core/v0.95.5/ws-events-worker.min.js"
   */
  const getBundlePath = async (options: {
    humanInTheLoopURL?: string;
    serviceDiscoveryURL?: string;
  }) => {
    const { humanInTheLoopURL = '', serviceDiscoveryURL = '' } = options;
    const getURL = (url: string) => {
      return `${url}/core/v${HITL_PKG_VERSION}/${WORKER_BUNDLE}`;
    };

    if (humanInTheLoopURL) {
      return getURL(humanInTheLoopURL);
    }

    const params = `serviceName=${HITL_UI_SERVICE}`;
    const discoveryUrl = `${serviceDiscoveryURL}/api/v2?${params}`;
    const response = await fetch(discoveryUrl);
    const discovery = await response.json();
    return getURL(discovery.url);
  };

  /** Local bundles have static name and are served from a static URL like "localhost:5500/" */
  const getLocalBundlePath = () => {
    return `${__webpack_public_path__}${WORKER_BUNDLE}`;
  };

  const initializeWSEventsWorker = async (args: {
    token: string;
    humanInTheLoopURL: string;
    humanInTheLoopApiURL: string;
    serviceDiscoveryURL: string;
    emBusURL: string;
    libraryRootPath: string;
    libraryAccountId: string;
    isDebug?: boolean;
    isWSDebug?: boolean;
  }) => {
    const { token, humanInTheLoopURL, humanInTheLoopApiURL, serviceDiscoveryURL, emBusURL, isDebug, isWSDebug } = args;
    context.isDebug = isDebug || false;
    cleanUpWorker();

    /* TODO: During development workers are served from a local directory */
    const workerPath = HITL_LOCALHOST_PATH
      ? getLocalBundlePath()
      : await getBundlePath({ humanInTheLoopURL, serviceDiscoveryURL });

    try {
      const response = await fetch(workerPath, { mode: 'cors' });
      const text = await response.text();
      const blob = new Blob([text], { type: 'text/javascript' });

      context.workerObjectURL = URL.createObjectURL(blob);
      context.worker = new Worker(context.workerObjectURL);

      context.worker.onmessage = onWorkerEvent;
      context.worker.postMessage({
        type: EVENT_TYPE.INITIALIZE,
        timestamp: new Date().toUTCString(),
        content: {
          token,
          humanInTheLoopApiURL,
          serviceDiscoveryURL,
          emBusURL,
          isDebug,
          isWSDebug,
        },
      });
    } catch (e) {
      console.error('Error while initializing "WebSocket Events" worker!', e);
      throw e;
    }
  };

  /**
   * When worker is created we initialize pings by default.
   * After reconnecting we should manually notify worker.
   */
  const initializeStatusPings = () => {
    if (!context.worker.postMessage) {
      return;
    }

    context.worker.postMessage({
      type: EVENT_TYPE.INITIALIZE_STATUS_PINGS,
      timestamp: new Date().toUTCString(),
      content: {},
    });
  };

  const updateWSToken = (token: string) => {
    if (!context.worker.postMessage) {
      return;
    }

    context.worker.postMessage({
      type: EVENT_TYPE.TOKEN_CHANGE,
      timestamp: new Date().toUTCString(),
      content: { token },
    });
  };

  const onWorkerEvent = (event: MessageEvent<EventData>) => {
    context.isDebug && console.debug('[WS Events Composable]', event.data);

    switch (event.data.type) {
      case EVENT_TYPE.EMIT_CONNECTION_ID:
        context.connectionId.value = event.data.content.connectionId;
      /* eslint-disable-next-line no-fallthrough */
      default: {
        const callbacks = context.callbacks[event.data.type] || [];
        for (const callback of callbacks) {
          callback(event.data);
        }
      }
    }
  };

  const cleanUpWorker = () => {
    if (context.workerObjectURL) {
      context.worker.terminate();
    }

    context.worker = {} as Worker;
    context.workerObjectURL = '';
    context.connectionId.value = '';
  };

  const subscribe = <T>(event: EVENT_TYPE, callback: EventCallback<T>) => {
    context.callbacks[event]
      ? context.callbacks[event].push(callback)
      : context.callbacks[event] = [callback];
  };

  const unsubscribe = <T>(event: EVENT_TYPE, callback: EventCallback<T>) => {
    const callbacks = context.callbacks[event];
    if (callbacks && callbacks.length) {
      context.callbacks[event] = callbacks.filter(i => i !== callback);
    }
  };

  const sendWSMessage = (message: IOutboundMessageData) => {
    return context.worker.postMessage({
      type: EVENT_TYPE.SEND_WS_MESSAGE,
      content: message,
    });
  };

  return {
    wsEventsWorker: context.worker,
    connectionId: context.connectionId,
    initializeWSEventsWorker,
    initializeStatusPings,
    updateWSToken,
    subscribe,
    unsubscribe,
    sendWSMessage,
    cleanUpWorker,
  };
};
